import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlProjectActionGroups, urlProjectActions } from '../../Constants';
import { IAction, IAppReduxStore, SlicePropsWithData } from '../store';
import {
    type ActionName,
    devOnClick,
    docTableActionsCallbacks,
} from '../../components/General/DocTable/DocTableActions';
import { safeJsonParse } from '../../util/DataUtils';
import { IFormFile } from '../../components/General/FormDesigner/FormDesigner';
import { ActionTypes, IFieldsAction } from '@eplass/formdesigner/domain/field.actions';
import { IProjectViewData } from './sliceProjectViews';
import { differenceWith, isEqual } from 'lodash';
import { FieldValues, UseFormSetValue } from 'react-hook-form';
import { IDocDefaultFields } from './documents/sliceProjectDocuments';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectActionsState = {
    data: null,
    loading: false,
    error: null,
    actionGroups: null,
    loadingGroups: false,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available actions in accordance with project and acl-role.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectActionsConfig: async (
        replicaId: string,
        thunkApi,
    ): Promise<IProjectActionsData[]> => {
        try {
            const request: RequestInit = {
                headers: {
                    replicaId,
                },
            };
            const response = await apiFetch(urlProjectActions, request);
            const result = (await response.json()) as IProjectActionsData[];
            const mappedResult = result.map(item => {
                return {
                    ...item,
                    extendedConfig: safeJsonParse(item?.extendedConfig as unknown as string, null),
                };
            });

            thunkApi.dispatch(getProjectActionGroupsConfig());

            return response.ok ? mappedResult : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    getProjectActionGroupsConfig: async (): Promise<IProjectActionGroupsData[]> => {
        try {
            const response = await apiFetch(urlProjectActionGroups);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectActionsState>) => {
        /**
         * Reducers for the 'getProjectActionsConfig' thunk.
         */

        builder.addCase(getProjectActionsConfig.pending, (state: Draft<IProjectActionsState>) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectActionsConfig.fulfilled,
            (state: Draft<IProjectActionsState>, action: IAction<IProjectActionsData[]>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectActionsConfig.rejected,
            (state: Draft<IProjectActionsState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );

        /**
         * Reducers for the 'getProjectActionGroupsConfig' thunk.
         */

        builder.addCase(
            getProjectActionGroupsConfig.pending,
            (state: Draft<IProjectActionsState>) => {
                state.loadingGroups = true;
                state.error = null;
            },
        );
        builder.addCase(
            getProjectActionGroupsConfig.fulfilled,
            (state: Draft<IProjectActionsState>, action: IAction<IProjectActionGroupsData[]>) => {
                state.loadingGroups = false;
                state.error = null;
                state.actionGroups = action.payload;
            },
        );
        builder.addCase(
            getProjectActionGroupsConfig.rejected,
            (state: Draft<IProjectActionsState>, action: IAction<SerializedError>) => {
                state.loadingGroups = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectActionsConfig = createAsyncThunk(
    'project/actions/permitted',
    thunks.getProjectActionsConfig,
);

export const getProjectActionGroupsConfig = createAsyncThunk(
    'project/actiongroups',
    thunks.getProjectActionGroupsConfig,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectActionsState extends SlicePropsWithData<IProjectActionsData[]> {
    actionGroups: IProjectActionGroupsData[];
    loadingGroups: boolean;
}

export interface IActionCallbackArguments {
    replicaId?: string;
    universalId?: string;
    idDocIndex?: string;
    fileName?: string;
    fieldName?: string;
    fieldIndex?: string;
    documentIds?: string[];
    documentNumber?: string;
    reportName?: string;
    currentView?: IProjectViewData;
    areaName?: string;
    viewName?: string;
    protocolName?: string;
    referencedMainDocId?: string;
    actionArgument?: unknown;
    actionKey?: string;
    jsonSchema?: string;
    formFieldName?: string;
    changedFormValues?: FieldValues;
    setValue?: UseFormSetValue<FieldValues>;
    document?: IDocDefaultFields;
    file?: string;
}

/**
 * Interface that describes data returned from the backend for actions.
 */
export interface IProjectActionsData {
    /** Area Name. */
    name: ActionName;

    /** Dictionary of translations for the area's display name. */
    translations: { [id: string]: string };

    /** SVG Icon for the View Area. */
    icon: string;

    /** Displays if action configurable. */
    configurable: boolean;

    /** Name of action group.  */
    actionGroups: string | null;

    /** Additional config for Area. */
    extendedConfig?: IExtendedConfig | null;
}

/**
 * Interface that describes data returned from the backend for actions groups.
 */
export interface IProjectActionGroupsData {
    /** Actions group name. */
    name: ActionName;

    /** Dictionary of translations for the actions group name. */
    translations: { [id: string]: string };

    /** SVG Icon for the actions group. */
    icon: string;

    /** Icon's color for the actions group. */
    color: string;
}

/**
 * Interface that describes extendedConfig after parsing.
 */
export interface IExtendedConfig {
    areaheader?: boolean;
    kebab?: boolean;
    quickaction?: boolean;
    snackbar?: boolean;
    wysiwyg?: boolean;
    minDocs?: number;
    maxDocs?: number;
    dynamicAction?: boolean;
    databaseAction?: boolean;
    AllowedFileFormats?: string;
    AllowedDataModelFileFormats?: string;
}

/**
 * Interface that describes DocTableActionsConfig.
 */
export interface IDocTableActionsConfig {
    /** Dictionary of translations for the area's display name. */
    translations: { [id: string]: string };

    /** SVG Icon for the View Area. */
    icon: string;

    /** Callback function */
    onClick: (actionData: IActionCallbackArguments) => void;
}

/**
 * Create the redux slice.
 */
export const projectActionsSlice = createSlice({
    name: 'projectActions',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

export const getPermittedActions = (state: IAppReduxStore) => state.projectActions.data;
export const getActionGroups = (state: IAppReduxStore) => state.projectActions.actionGroups;
export const getDocTableActionsConfig = (
    state: IAppReduxStore,
): Record<ActionName, IDocTableActionsConfig> | null => {
    return (
        state.projectActions?.data?.reduce((obj, ac) => {
            obj[ac.name] = {
                icon: ac.icon,
                onClick: docTableActionsCallbacks[ac.name] ?? devOnClick,
                translations: ac.translations,
            };
            return obj;
        }, {} as Record<ActionName, IDocTableActionsConfig> | null) ?? null
    );
};

export const getActionsForFormdesigner = (
    state: IAppReduxStore,
    params: { replicaId: string; universalId: string; jsonSchema: string },
): IFieldsAction<Record<string, string>>[] | null => {
    return (
        state.projectActions?.data
            ?.filter(item => item.extendedConfig?.wysiwyg)
            .map(({ name, icon, configurable, translations, extendedConfig }) => ({
                name,
                icon,
                configurable,
                localizedTitle: translations,
                actionType: extendedConfig?.dynamicAction
                    ? ActionTypes.DynamicAction
                    : ActionTypes.Action,
                callback: (
                    formValue: unknown,
                    actionName: string,
                    fieldName: string,
                    changedFormValues: FieldValues,
                    setValue: UseFormSetValue<FieldValues>,
                ) => {
                    const formWithValue = formValue as { value: unknown };
                    const genericAction = extendedConfig?.dynamicAction
                        ? docTableActionsCallbacks.formdesignersDynamicAction
                        : devOnClick;
                    const commonParams = {
                        jsonSchema: params.jsonSchema,
                        actionKey: actionName,
                        actionArgument: formValue,
                        replicaId: params.replicaId,
                        universalId: params.universalId,
                        formFieldName: fieldName,
                        changedFormValues,
                        setValue,
                    };
                    if (!formWithValue?.value || typeof formWithValue?.value === 'string') {
                        return (docTableActionsCallbacks?.[name] || genericAction)({
                            ...commonParams,
                        });
                    } else {
                        const {
                            replicaId,
                            IdDocIndex,
                            fileName,
                            UniversalID,
                            fieldName = 'File',
                            fieldIndex = '0',
                        } = (formValue as IFormFile).value;

                        return (docTableActionsCallbacks?.[name] || genericAction)({
                            ...commonParams,
                            replicaId,
                            universalId: UniversalID,
                            idDocIndex: IdDocIndex,
                            fileName,
                            fieldName,
                            fieldIndex,
                        });
                    }
                },
            })) ?? []
    );
};

export const getActionsListGroupByActionGroup = (
    actions: IProjectActionsData[],
    groups: IProjectActionGroupsData[],
) => {
    let actionList = [...actions];
    const actionsListGroupByActionGroup = [];

    for (let i = 0; i < actionList.length; i++) {
        if (actionList[i].actionGroups) {
            const groupActions = actionList.filter(
                ac => ac.actionGroups === actionList[i].actionGroups,
            );
            if (groupActions.length > 1) {
                const newActionsGroup = {
                    ...groups.find(actionGroup => actionGroup.name === actionList[i].actionGroups),
                    groupActions,
                };
                actionList = differenceWith(actionList, groupActions, isEqual);
                actionsListGroupByActionGroup.push(newActionsGroup);
                i--;
            } else {
                actionsListGroupByActionGroup.push(actionList[i]);
            }
        } else {
            actionsListGroupByActionGroup.push(actionList[i]);
        }
    }
    return actionsListGroupByActionGroup;
};

/**
 * Export slice reducer as default.
 */
export default projectActionsSlice.reducer;
